import React, { useState } from "react";
import { ChevronDown, FileText, Palette, Building2, Download } from "lucide-react";
import { SITE_CONFIG } from "../config/site";
import { useContent, renderHighlightedTitle } from "../lib/useContent";
import { PageSEO } from "../components/PageSEO";

const CCN_PARAGRAPHS = [
  "Un Centro Commerciale Naturale (CCN) è una rete di attività commerciali, artigianali, turistiche, di servizio, enti e associazioni che collaborano per valorizzare il centro urbano e promuovere lo sviluppo economico e sociale del territorio.",
  "A differenza dei tradizionali centri commerciali, il Centro Commerciale Naturale non è un edificio unico, ma un insieme di attività distribuite nel tessuto cittadino che lavorano in rete per offrire un'esperienza condivisa a cittadini e visitatori.",
  "Attraverso iniziative comuni, eventi, campagne di comunicazione e progetti di valorizzazione del territorio, il CCN contribuisce a:",
];

const CCN_BULLETS = [
  "sostenere il commercio locale;",
  "valorizzare il centro storico;",
  "promuovere le eccellenze del territorio;",
  "favorire la collaborazione tra imprese;",
  "migliorare l'esperienza di residenti e visitatori.",
];

const CCN_SANLURI_PARAGRAPHS = [
  "Il Centro Commerciale Naturale Sanluri nasce dalla volontà di creare una rete tra le realtà economiche e associative del territorio.",
  "Ne fanno parte negozi, pubblici esercizi, artigiani, professionisti, attività turistiche, enti e associazioni che condividono la stessa visione: rendere Sanluri un luogo sempre più dinamico, accogliente e ricco di opportunità.",
  "Ogni attività aderente contribuisce a costruire una comunità capace di valorizzare il territorio attraverso la qualità dei servizi, la collaborazione e l'organizzazione di iniziative dedicate a cittadini e visitatori.",
  "Far parte del CCN significa investire insieme nel futuro del paese, sostenendo il commercio di prossimità e creando valore per tutta la comunità.",
];

const STATUTO_TEXT = [
  "Lo Statuto disciplina l'organizzazione, gli obiettivi e il funzionamento del Centro Commerciale Naturale Sanluri.",
  "Al suo interno sono riportati i principi che regolano la vita associativa, i diritti e i doveri degli associati e le modalità di partecipazione alle attività del Centro Commerciale Naturale.",
];

const LOGO_TEXT =
  "Il logo del Centro Commerciale Naturale Sanluri rappresenta il legame tra il territorio, la comunità e le attività economiche che ne fanno parte. È il simbolo dell'identità del progetto e viene utilizzato in tutte le comunicazioni ufficiali del Centro Commerciale Naturale.";

const AccordionItem = ({ id, icon: Icon, title, open, onToggle, children, testid }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-3xl overflow-hidden transition-all">
    <button
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`panel-${id}`}
      data-testid={testid}
      className="w-full flex items-center gap-5 px-6 md:px-10 py-6 md:py-8 text-left group"
    >
      <div className={`w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors ${open ? "bg-[#12964A] text-white" : "bg-[#F0FAF3] text-[#12964A] group-hover:bg-[#E1F3E8]"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-1">Sezione</div>
        <h2 className="font-display font-bold text-xl md:text-2xl text-[#1F2937] leading-tight">{title}</h2>
      </div>
      <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 flex-shrink-0 ${open ? "rotate-180 text-[#12964A]" : ""}`} />
    </button>
    <div id={`panel-${id}`} className={`grid transition-all duration-500 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
      <div className="overflow-hidden">
        <div className="px-6 md:px-10 pb-8 md:pb-10 pt-2 border-t border-[#F1F5F9]">{children}</div>
      </div>
    </div>
  </div>
);

const About = () => {
  const [open, setOpen] = useState("ccn");
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));
  const { field, seo } = useContent("about");
  const heroPretitle = field("hero", "pretitle", "Chi siamo");
  const heroHeadline = field("hero", "headline", "Il commercio cresce quando cresce la comunità.");
  const heroHighlight = field("hero", "highlight_terms", ["la comunità"]);
  const heroDescription = field("hero", "description", "Il Centro Commerciale Naturale Sanluri riunisce attività commerciali, artigianali, turistiche, professionisti, enti e associazioni con un obiettivo comune: valorizzare il centro cittadino, sostenere le imprese locali e rendere Sanluri sempre più viva, accogliente e attrattiva.");
  const heroImage = field("hero", "image_url", "/brand/direttivo.webp");
  const heroImageAlt = field("hero", "image_alt", "Il Direttivo del Centro Commerciale Naturale Sanluri");
  const heroImageCaption = field("hero", "image_caption", "Il Direttivo del Centro Commerciale Naturale Sanluri.");

  return (
    <div className="pt-32 pb-24 bg-[#F8F9FA] min-h-screen">
      <PageSEO seo={seo} defaults={{ title: "Chi siamo — CCN Sanluri", description: "Il Centro Commerciale Naturale di Sanluri riunisce commercianti, artigiani e associazioni per far vivere il centro." }} />
      {/* HERO — istituzionale */}
      <section className="max-w-[1300px] mx-auto px-6 lg:px-10 mb-16 md:mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            {heroPretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-6">{heroPretitle}</div>}
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight text-[#1F2937]">
              {renderHighlightedTitle(heroHeadline, heroHighlight)}
            </h1>
            {heroDescription !== "" && (
              <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-xl">{heroDescription}</p>
            )}
          </div>
          <div className="lg:col-span-6">
            <figure>
              <div className="rounded-3xl overflow-hidden bg-slate-100 shadow-sm">
                <img
                  src={heroImage}
                  alt={heroImageAlt}
                  loading="eager"
                  className="w-full h-full object-cover aspect-[4/3]"
                />
              </div>
              {heroImageCaption !== "" && (
                <figcaption className="mt-4 text-xs uppercase tracking-[0.18em] text-slate-500 text-center">
                  {heroImageCaption}
                </figcaption>
              )}
            </figure>
          </div>
        </div>
      </section>

      {/* ACCORDION */}
      <section className="max-w-[1000px] mx-auto px-6 lg:px-10 space-y-4" data-testid="about-accordion">

        {/* 1. Cos'è un CCN */}
        <AccordionItem
          id="ccn"
          icon={Building2}
          title="Cos'è un Centro Commerciale Naturale"
          open={open === "ccn"}
          onToggle={() => toggle("ccn")}
          testid="about-accordion-ccn"
        >
          <div className="space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
            {CCN_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
            <ul className="list-disc pl-6 space-y-1.5 marker:text-[#12964A]">
              {CCN_BULLETS.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <h3 className="font-display font-bold text-xl md:text-2xl text-[#1F2937] pt-6">Il CCN Sanluri</h3>
            {CCN_SANLURI_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </AccordionItem>

        {/* 2. Statuto */}
        <AccordionItem
          id="statuto"
          icon={FileText}
          title="Statuto dell'Associazione"
          open={open === "statuto"}
          onToggle={() => toggle("statuto")}
          testid="about-accordion-statuto"
        >
          <div className="space-y-4 text-slate-600 text-base md:text-lg leading-relaxed">
            {STATUTO_TEXT.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-8">
            {SITE_CONFIG.STATUTE_PDF_URL ? (
              <a
                href={SITE_CONFIG.STATUTE_PDF_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
                data-testid="statuto-download"
              >
                <Download className="w-4 h-4" /> Scarica lo Statuto (PDF)
              </a>
            ) : (
              <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="italic">Statuto in fase di aggiornamento.</span>
              </div>
            )}
          </div>
        </AccordionItem>

        {/* 3. Il nostro logo — versione semplificata */}
        <AccordionItem
          id="logo"
          icon={Palette}
          title="Il nostro logo"
          open={open === "logo"}
          onToggle={() => toggle("logo")}
          testid="about-accordion-logo"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                {LOGO_TEXT}
              </p>
            </div>
            <div className="order-1 md:order-2">
              <div className="bg-white border border-[#F1F5F9] rounded-3xl p-8 md:p-10 flex items-center justify-center">
                <img
                  src="/brand/ccn-logo.png"
                  alt="Logo Centro Commerciale Naturale Sanluri"
                  loading="lazy"
                  className="max-w-full max-h-64 md:max-h-72 w-auto h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </AccordionItem>

      </section>
    </div>
  );
};

export default About;
